import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, TextField, InputAdornment, IconButton, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, Autocomplete,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { alpha } from '@mui/material/styles';
import { getPersonnel as getPersonnelSql } from '../../../services/migration/personnelDataService';
import { useAuth } from '../../../contexts/AuthContext';
import { smartWarehouseService } from '../services/smartWarehouseService';
import { waveService } from '../services/waveService';
import { screenService } from '../services/screenService';
import { wmsPersonnelPermissionService } from '../services/wmsPersonnelPermissionService';
import WaveSummaryCards from '../components/WaveSummaryCards';
import WaveTable from '../components/WaveTable';
import WaveDetailModal from '../components/WaveDetailModal';
import WaveSetupWizard from '../components/WaveSetupWizard';
import SmartWaveModal from '../components/SmartWaveModal';

interface Warehouse { id: string; name: string; }

interface WaveSummary {
  totalWaves: number; createdWaves: number; inProgressWaves: number;
  completedWaves: number; cancelledWaves: number;
  totalOrders: number; totalItems: number; pickedItems: number;
}

interface WmsWave {
  id: string; waveNumber: string; waveType: string; status: string;
  assignedPickerId?: string; assignedPickerName?: string; assignedPersonnelId?: string;
  cartId?: string; cartCode?: string;
  totalOrders: number; totalItems: number; pickedItems: number;
  createdAt: string; startedAt?: string; completedAt?: string;
}

interface PersonnelOption {
  id: string;
  name: string;
  email?: string;
}

const WMS_WAVE_CREATED_EVENT = 'wms-wave-created';
const WMS_WAVE_CREATED_STORAGE_KEY = 'wms-wave-created-ts';
const WMS_WAVE_STATUS_EVENT = 'wms-wave-status-changed';
const WMS_WAVE_STATUS_STORAGE_KEY = 'wms-wave-status-ts';

// Sipariş Havuzu'ndaki bekleyen-only kuraliyla ayni mantik:
// sadece "Created" statusunde ve toplayicisi henuz atanmamis dalgalar checkbox ile secilebilir.
// Boylece tablodan zaten atanmis veya devam eden dalgalari yanlislikla toplu atama icin secemeyiz.
const isWaveSelectable = (wave: WmsWave): boolean => {
  if (wave.status !== 'Created') return false;
  const hasPicker = !!(
    wave.assignedPickerId ||
    wave.assignedPersonnelId ||
    (wave.assignedPickerName && wave.assignedPickerName.trim().length > 0)
  );
  return !hasPicker;
};

const WmsWaveManagement: React.FC = () => {
  const { userDetails } = useAuth();
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [waves, setWaves] = useState<WmsWave[]>([]);
  const [summary, setSummary] = useState<WaveSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedWaveIds, setSelectedWaveIds] = useState<Set<string>>(new Set());
  const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false);
  const [bulkOptions, setBulkOptions] = useState<PersonnelOption[]>([]);
  const [bulkSelectedPersonnelId, setBulkSelectedPersonnelId] = useState('');
  const [bulkLoadingOptions, setBulkLoadingOptions] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [bulkInfoMessage, setBulkInfoMessage] = useState('');

  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedWaveId, setSelectedWaveId] = useState<string | null>(null);
  const [isSetupWizardOpen, setIsSetupWizardOpen] = useState(false);
  const [setupInitialStep, setSetupInitialStep] = useState<number | undefined>(undefined);
  const [isSmartWaveModalOpen, setIsSmartWaveModalOpen] = useState(false);
  const [targetWave, setTargetWave] = useState<WmsWave | null>(null);

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean; title: string; message: string; action: () => Promise<void>;
  }>({ open: false, title: '', message: '', action: async () => {} });
  const [confirmLoading, setConfirmLoading] = useState(false);
  const hasInitialLoadRef = useRef(false);
  const lastRefreshAtRef = useRef(0);

  useEffect(() => {
    smartWarehouseService.getAllList().then(list => {
      const mapped = list.map((w) => ({
        id: w.id,
        name: w.warehouseName,
      }));
      setWarehouses(mapped);
      if (mapped.length > 0) {
        setSelectedWarehouseId((prev) => prev || mapped[0].id);
      }
    }).catch(() => {});
  }, []);

  const loadWaves = useCallback(async (showLoading = false) => {
    if (!selectedWarehouseId) return;
    if (showLoading) setLoading(true);
    try {
      const res = await waveService.getByWarehouse(selectedWarehouseId, {
        page, pageSize, status: statusFilter, waveType: typeFilter, search: searchText,
      });
      const data = res.data?.data || res.data;
      setWaves(data?.items || data || []);
      setTotalCount(data?.totalCount ?? data?.length ?? 0);
    } catch { setWaves([]); setTotalCount(0); }
    if (showLoading) setLoading(false);
  }, [selectedWarehouseId, page, pageSize, statusFilter, typeFilter, searchText]);

  const loadSummary = useCallback(async () => {
    if (!selectedWarehouseId) return;
    try {
      const res = await waveService.getSummary(selectedWarehouseId);
      setSummary(res.data?.data || res.data);
    } catch { setSummary(null); }
  }, [selectedWarehouseId]);

  useEffect(() => {
    loadWaves(!hasInitialLoadRef.current);
    loadSummary();
    hasInitialLoadRef.current = true;
  }, [loadWaves, loadSummary]);

  const refresh = useCallback(() => {
    const now = Date.now();
    // Birden fazla event (storage/focus/visibility/custom event) arka arkaya gelince
    // aynı anda tekrar tekrar reload olmasın.
    if (now - lastRefreshAtRef.current < 1200) return;
    lastRefreshAtRef.current = now;
    loadWaves(false);
    loadSummary();
  }, [loadWaves, loadSummary]);

  useEffect(() => {
    const handleWaveRefreshSignal = (event: Event) => {
      const custom = event as CustomEvent<{ warehouseId?: string }>;
      const createdWarehouseId = custom?.detail?.warehouseId;
      if (!createdWarehouseId || !selectedWarehouseId || createdWarehouseId === selectedWarehouseId) {
        refresh();
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === WMS_WAVE_CREATED_STORAGE_KEY || event.key === WMS_WAVE_STATUS_STORAGE_KEY) {
        refresh();
      }
    };

    window.addEventListener(WMS_WAVE_CREATED_EVENT, handleWaveRefreshSignal as EventListener);
    window.addEventListener(WMS_WAVE_STATUS_EVENT, handleWaveRefreshSignal as EventListener);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(WMS_WAVE_CREATED_EVENT, handleWaveRefreshSignal as EventListener);
      window.removeEventListener(WMS_WAVE_STATUS_EVENT, handleWaveRefreshSignal as EventListener);
      window.removeEventListener('storage', handleStorage);
    };
  }, [selectedWarehouseId, refresh]);

  useEffect(() => {
    if (!selectedWarehouseId) return;
    const handleVisibilityRefresh = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', handleVisibilityRefresh);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityRefresh);
    };
  }, [selectedWarehouseId, refresh]);

  const handleWarehouseChange = (wId: string) => {
    setSelectedWarehouseId(wId);
    setPage(1);
    setSelectedWaveIds(new Set());
  };

  const handleSummaryFilterClick = (status: string) => {
    setStatusFilter(status);
    setTypeFilter('');
    setPage(1);
    setSelectedWaveIds(new Set());
  };

  const handleToggleSelect = (id: string) => {
    setSelectedWaveIds((prev) => {
      const wave = waves.find((w) => w.id === id);
      // Atanmis veya devam eden dalgalar checkbox ile secilemez (Sipariş Havuzu kuraliyla ayni).
      if (!wave || !isWaveSelectable(wave)) return prev;
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    const selectableIds = waves.filter(isWaveSelectable).map((w) => w.id);
    const allSelected =
      selectableIds.length > 0 && selectableIds.every((id) => selectedWaveIds.has(id));
    setSelectedWaveIds((prev) => {
      const next = new Set(prev);
      selectableIds.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  useEffect(() => {
    setSelectedWaveIds((prev) => {
      // Yalnizca hala secilebilir durumda olan dalgalari secimde tut;
      // arka planda statusu InProgress'e gecmis veya toplayici atanmis dalgalari secimden cikar.
      const validIds = new Set(waves.filter(isWaveSelectable).map((w) => w.id));
      return new Set(Array.from(prev).filter((id) => validIds.has(id)));
    });
  }, [waves]);

  const handleViewDetail = (id: string) => {
    setSelectedWaveId(id);
    setIsDetailOpen(true);
  };

  const handleSetup = (wave: WmsWave, step?: number) => {
    setTargetWave(wave);
    setSetupInitialStep(step);
    setIsSetupWizardOpen(true);
  };

  const handleCancel = (id: string) => {
    setConfirmDialog({
      open: true,
      title: 'İş Emri İptali',
      message: 'Bu iş emrini iptal etmek istediğinize emin misiniz? Siparişler "Bekleyen" durumuna geri döner.',
      action: async () => {
        await waveService.cancel(id);
        refresh();
      },
    });
  };

  const handleDelete = (id: string) => {
    setConfirmDialog({
      open: true,
      title: 'İş Emri Silme',
      message: 'Bu iş emrini silmek istediğinize emin misiniz?',
      action: async () => {
        await waveService.delete(id);
        refresh();
      },
    });
  };

  const executeConfirmAction = async () => {
    setConfirmLoading(true);
    try {
      await confirmDialog.action();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'İşlem başarısız');
    }
    setConfirmLoading(false);
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };

  const bulkSelectedOption = useMemo(
    () => bulkOptions.find((p) => p.id === bulkSelectedPersonnelId) || null,
    [bulkOptions, bulkSelectedPersonnelId]
  );

  useEffect(() => {
    if (!isBulkAssignModalOpen) return;

    const loadBulkOptions = async () => {
      setBulkLoadingOptions(true);
      setBulkError('');
      setBulkInfoMessage('');
      try {
        if (!userDetails?.companyId) {
          setBulkOptions([]);
          setBulkInfoMessage('Sirket bilgisi bulunamadi.');
          return;
        }

        // Faz 3: personel listesi SQL'den (personnelDataService).
        const sqlPersonnel = await getPersonnelSql(userDetails.companyId);
        const basePersonnel: PersonnelOption[] = (sqlPersonnel || [])
          .map((raw: any) => ({ id: raw.id, name: raw?.name || 'Isimsiz Personel', email: raw?.email }))
          .filter((p) => !!p.id && !!p.name);

        const screensResp = await screenService.getScreens('picking', selectedWarehouseId);
        const screens = screensResp.data?.data || screensResp.data || [];
        const pickingScreenIds: string[] = screens.map((s: any) => s.id || s.Id).filter(Boolean);

        if (pickingScreenIds.length === 0) {
          setBulkOptions(basePersonnel);
          setBulkInfoMessage('Bu depoda Toplama ekrani bulunamadi. Tum personel listelendi.');
          return;
        }

        const checks = await Promise.all(
          basePersonnel.map(async (personnel) => {
            const allowedScreens = await wmsPersonnelPermissionService.getMyWmsScreens(
              userDetails.companyId as string,
              personnel.id
            );
            const hasAccess = allowedScreens.some((screenId) => pickingScreenIds.includes(screenId));
            return hasAccess ? personnel : null;
          })
        );
        setBulkOptions(checks.filter((item): item is PersonnelOption => !!item));
      } catch (err: any) {
        setBulkError(err?.response?.data?.message || 'Personel listesi yuklenemedi.');
        setBulkOptions([]);
      } finally {
        setBulkLoadingOptions(false);
      }
    };

    loadBulkOptions();
  }, [isBulkAssignModalOpen, selectedWarehouseId, userDetails?.companyId]);

  const handleBulkAssign = async () => {
    if (!bulkSelectedOption) {
      setBulkError('Toplayici secimi zorunludur.');
      return;
    }

    // Yalnizca hala secilebilir durumda kalmis dalgalari toplu atama listesine aliyoruz;
    // arka planda baska bir kullanici tarafindan atanmis veya devam etmis dalgalari atliyoruz.
    const selectedWaves = waves.filter(
      (w) => selectedWaveIds.has(w.id) && isWaveSelectable(w)
    );
    if (selectedWaves.length === 0) {
      setBulkError('Lutfen en az bir is emri secin.');
      return;
    }

    setBulkSaving(true);
    setBulkError('');
    setBulkInfoMessage('');
    const isLegacyFallbackStatus = (status?: number) => status === 404 || status === 405;

    const successWaveIds: string[] = [];
    const failures: { waveNumber: string; message: string }[] = [];

    // Toplu atamada bir dalga basarisiz olsa bile diger dalgalara devam ediyoruz.
    // Boylece "3. dalgada 500 dondu" senaryosunda 1-2 atanmis, 4-5 atlanmis kalmiyor.
    for (const wave of selectedWaves) {
      try {
        try {
          await waveService.assignPersonnelToWave({
            WaveId: wave.id,
            PersonnelId: bulkSelectedOption.id,
            PersonnelName: bulkSelectedOption.name,
          });
        } catch (err: any) {
          if (isLegacyFallbackStatus(err?.response?.status)) {
            await waveService.assignPicker({
              WaveId: wave.id,
              PickerId: bulkSelectedOption.id,
              PickerName: bulkSelectedOption.name,
            });
          } else {
            throw err;
          }
        }
        successWaveIds.push(wave.id);
      } catch (err: any) {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          'Atama yapilamadi';
        failures.push({ waveNumber: wave.waveNumber || wave.id.slice(0, 8), message });
      }
    }

    setBulkSaving(false);

    if (failures.length === 0) {
      setSelectedWaveIds(new Set());
      setIsBulkAssignModalOpen(false);
      refresh();
      return;
    }

    // Kismi basarili durumlarda atanmis dalgalari secimden cikariyoruz ki kullanici
    // sadece basarisizlari yeniden deneyebilsin. Modal acik kaliyor.
    if (successWaveIds.length > 0) {
      setSelectedWaveIds((prev) => {
        const next = new Set(prev);
        successWaveIds.forEach((id) => next.delete(id));
        return next;
      });
      refresh();
    }

    const failureSummary = failures
      .slice(0, 3)
      .map((f) => `${f.waveNumber}: ${f.message}`)
      .join(' | ');
    const extraFailureCount = failures.length > 3 ? ` (+${failures.length - 3} iş emri daha)` : '';

    if (successWaveIds.length === 0) {
      setBulkError(`Toplu atama basarisiz. ${failureSummary}${extraFailureCount}`);
    } else {
      setBulkError('');
      setBulkInfoMessage(
        `${successWaveIds.length} iş emri atandı, ${failures.length} iş emri atanamadı. ${failureSummary}${extraFailureCount}`
      );
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 2,
        }}
      >
        <Typography variant="h5" fontWeight={700}>İş Emirleri</Typography>
        <FormControl
          size="small"
          sx={{
            minWidth: 230,
            bgcolor: 'background.paper',
            borderRadius: 2,
            '& .MuiOutlinedInput-root': { borderRadius: 2 },
          }}
        >
          <InputLabel>Depo</InputLabel>
          <Select
            value={selectedWarehouseId}
            label="Depo"
            onChange={(e) => handleWarehouseChange(e.target.value)}
            startAdornment={<WarehouseIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />}
          >
            {warehouses.map((w) => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <WaveSummaryCards summary={summary} onFilterClick={handleSummaryFilterClick} />

      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mb: 2,
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl
            size="small"
            sx={{
              minWidth: 140,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'background.paper',
              },
            }}
          >
            <InputLabel>Durum</InputLabel>
            <Select value={statusFilter} label="Durum" onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <MenuItem value="">Tümü</MenuItem>
              <MenuItem value="Created">Oluşturuldu</MenuItem>
              <MenuItem value="InProgress">Devam Ediyor</MenuItem>
              <MenuItem value="Completed">Tamamlandı</MenuItem>
              <MenuItem value="Cancelled">İptal</MenuItem>
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={{
              minWidth: 120,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'background.paper',
              },
            }}
          >
            <InputLabel>Tip</InputLabel>
            <Select value={typeFilter} label="Tip" onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
              <MenuItem value="">Tümü</MenuItem>
              <MenuItem value="Single">Tekli</MenuItem>
              <MenuItem value="Multi">Çoklu</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="İş emri no veya toplayıcı..."
            value={searchText}
            onChange={e => { setSearchText(e.target.value); setPage(1); }}
            sx={{
              minWidth: { xs: '100%', sm: 260 },
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'background.paper',
              },
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              endAdornment: searchText ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setSearchText(''); setPage(1); }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          startIcon={<AutoAwesomeIcon />}
          disabled={!selectedWarehouseId}
          onClick={() => setIsSmartWaveModalOpen(true)}
        >
          Akıllı İş Emri
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<PersonAddIcon />}
          disabled={selectedWaveIds.size === 0}
          onClick={() => {
            setBulkSelectedPersonnelId('');
            setBulkError('');
            setIsBulkAssignModalOpen(true);
          }}
        >
          Secilenleri Toplayiciya Ata ({selectedWaveIds.size})
        </Button>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          overflow: 'hidden',
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
          bgcolor: 'background.paper',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <WaveTable waves={waves} page={page} pageSize={pageSize} totalCount={totalCount}
            selectedIds={selectedWaveIds}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            onViewDetail={handleViewDetail}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
            isRowSelectable={isWaveSelectable}
            onSetup={handleSetup} onCancel={handleCancel} onDelete={handleDelete} />
        )}
      </Paper>

      {/* Detail Modal */}
      <WaveDetailModal open={isDetailOpen} waveId={selectedWaveId}
        onClose={() => setIsDetailOpen(false)} />

      {/* İş Emri Hazırlama Sihirbazı (Toplayıcı → Araba → Başlat) */}
      <WaveSetupWizard
        open={isSetupWizardOpen}
        wave={targetWave}
        warehouseId={selectedWarehouseId}
        initialStep={setupInitialStep}
        onClose={() => setIsSetupWizardOpen(false)}
        onRefresh={refresh}
      />

      {/* Smart Wave Modal */}
      <SmartWaveModal
        open={isSmartWaveModalOpen}
        warehouseId={selectedWarehouseId}
        onClose={() => setIsSmartWaveModalOpen(false)}
        onCreated={() => { setIsSmartWaveModalOpen(false); refresh(); }}
      />

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}>Vazgeç</Button>
          <Button variant="contained" color="primary" onClick={executeConfirmAction} disabled={confirmLoading}>
            {confirmLoading ? <CircularProgress size={18} /> : 'Onayla'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isBulkAssignModalOpen} onClose={() => setIsBulkAssignModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Toplu Toplayıcı Ata</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Secilen Is Emirleri: <strong>{selectedWaveIds.size}</strong>
            </Typography>

            {bulkInfoMessage && <Alert severity="warning">{bulkInfoMessage}</Alert>}

            <Autocomplete
              options={bulkOptions}
              value={bulkSelectedOption}
              loading={bulkLoadingOptions}
              getOptionLabel={(option) => option.name}
              onChange={(_event, value) => setBulkSelectedPersonnelId(value?.id || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Toplayici *"
                  placeholder="Personel secin"
                  autoFocus
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {bulkLoadingOptions ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            {bulkError && <Typography color="error" variant="body2">{bulkError}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsBulkAssignModalOpen(false)}>Iptal</Button>
          <Button variant="contained" onClick={handleBulkAssign} disabled={bulkSaving || !bulkSelectedPersonnelId}>
            {bulkSaving ? <CircularProgress size={18} /> : 'Toplu Ata'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WmsWaveManagement;
